'use client'

import { Box, Flex, styled } from '@/styled-system/jsx'
import { memo } from 'react'
import useGameSettings from '../shared/settings/useGameSettings'

const StyledInput = styled('input', {
  base: {
    appearance: 'none',
    position: 'absolute',
    top: 0,
    w: 'full',
    h: 5,
    bg: 'transparent',
    zIndex: 10,

    '&::-webkit-slider-runnable-track': {
      w: 'full',
      h: 5,
      cursor: 'pointer',
      bg: 'transparent',
      border: 'none',
      pointerEvents: 'none',
    },

    '&::-moz-range-track': {
      w: 'full',
      h: 5,
      cursor: 'pointer',
      bg: 'transparent',
      border: 'none',
      pointerEvents: 'none',
    },

    '&::-ms-track': {
      w: 'full',
      h: 5,
      cursor: 'pointer',
      bg: 'transparent',
      border: 'none',
      pointerEvents: 'none',
    },

    '&::-webkit-slider-thumb': {
      mt: -0.5,
      w: 2.5,
      h: 10,
      border: 'none',
      bg: '#D9D9D9',
      cursor: 'grab',
      appearance: 'none',
      borderRadius: 'full',
      boxShadow:
        '0 4px 6px -1px rgba(27, 23, 23, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-8px)',
      pointerEvents: 'auto',

      '&:active': {
        cursor: 'grabbing',
      },
    },

    '&::-moz-range-thumb': {
      mt: -0.5,
      w: 2.5,
      h: 10,
      border: 'none',
      bg: '#D9D9D9',
      cursor: 'grab',
      appearance: 'none',
      borderRadius: 'full',
      boxShadow:
        '0 4px 6px -1px rgba(27, 23, 23, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-8px)',
      pointerEvents: 'auto',

      '&:active': {
        cursor: 'grabbing',
      },
    },

    '&::-ms-thumb': {
      mt: -0.5,
      w: 2.5,
      h: 10,
      border: 'none',
      bg: '#D9D9D9',
      cursor: 'grab',
      appearance: 'none',
      borderRadius: 'full',
      boxShadow:
        '0 4px 6px -1px rgba(27, 23, 23, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-8px)',
      pointerEvents: 'auto',

      '&:active': {
        cursor: 'grabbing',
      },
    },

    '&::-ms-fill-lower': {
      w: 'full',
      h: 5,
      cursor: 'pointer',
      bg: 'transparent',
      border: 'none',
      pointerEvents: 'none',
    },

    '&::-ms-fill-upper': {
      w: 'full',
      h: 5,
      cursor: 'pointer',
      bg: 'transparent',
      border: 'none',
      pointerEvents: 'none',
    },

    _focus: {
      outline: 'none',

      '&::-webkit-slider-runnable-track': {
        w: 'full',
        h: 5,
        cursor: 'pointer',
        bg: 'transparent',
        border: 'none',
        pointerEvents: 'none',
      },

      '&::-ms-fill-lower': {
        w: 'full',
        h: 5,
        cursor: 'pointer',
        bg: 'transparent',
        border: 'none',
        pointerEvents: 'none',
      },

      '&::-ms-fill-upper': {
        w: 'full',
        h: 5,
        cursor: 'pointer',
        bg: 'transparent',
        border: 'none',
        pointerEvents: 'none',
      },
    },

    _disabled: {
      '&::-webkit-slider-thumb': {
        cursor: 'not-allowed',
      },

      '&::-moz-range-thumb': {
        cursor: 'not-allowed',
      },

      '&::-ms-thumb': {
        cursor: 'not-allowed',
      },
    },
  },
})

export type DiceSliderProps = {
  diceRef?: React.RefObject<HTMLDivElement>
  condition: 'under' | 'over'
  target: number
  result: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  showResult?: boolean
  isDisabled?: boolean
}

const DiceSlider = memo(function DiceSlider({
  diceRef,
  condition,
  target,
  result,
  onChange,
  showResult,
  isDisabled,
}: DiceSliderProps) {
  const instantBetEnabled = useGameSettings((state) => state.instantBetEnabled)

  return (
    <Box position='relative' w='full' maxW='730px' my={16}>
      <Box
        position='relative'
        w='full'
        p={4}
        bgColor='neutral.300'
        borderRadius='2xl'
        boxShadow='md'
      >
        <Box position='absolute' bottom='100%' left='1rem' right='1rem'>
          {[0, 25, 50, 75, 100].map((mark) => (
            <Box
              key={mark}
              position='absolute'
              _after={{
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                w: 0.5,
                h: 2.5,
                bgColor: 'neutral.300',
                transform: 'translateX(-50%)',
              }}
              style={{
                left: `${mark}%`,
              }}
            >
              <Box
                position='absolute'
                bottom='1rem'
                left='50%'
                textStyle='sm'
                fontWeight='bold'
                transform='translateX(-50%)'
              >
                {mark}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Dice */}
        <Flex
          position='absolute'
          bottom='100%'
          left={0}
          right={0}
          transitionProperty='transform'
          transitionDuration='var(--dice-slide-transition)'
          willChange='transform'
          pointerEvents='none'
          zIndex={5}
          style={
            {
              '--dice-slide-transition': instantBetEnabled ? '0s' : '0.3s',
              transform: `translateX(${result}%)`,
            } as React.CSSProperties
          }
        >
          <Box
            ref={diceRef}
            position='relative'
            color={showResult ? 'success' : 'text.secondary'}
            opacity={showResult ? 1 : 0}
            transform='translateX(-50%) scale(0.8)'
            transformOrigin='center bottom'
            transition='all var(--dice-scale-transition)'
            css={{
              '&[data-state=show]': {
                transform: 'translateX(-50%)',
                opacity: 1,
              },
              '&[data-status=win], &[data-status=lose]': {
                animation: `expand var(--dice-scale-transition)`,
              },
              '&[data-status=win]': {
                color: 'success',
              },
              '&[data-status=lose]': {
                color: 'error',
              },
            }}
            style={
              {
                '--dice-scale-transition': instantBetEnabled ? '0s' : '0.5s',
              } as React.CSSProperties
            }
          >
            <Box
              w={16}
              h={16}
              borderRadius='xl'
              bg='text.primary'
              boxShadow='0 0 3px rgba(25, 25, 25, 0.1)'
              transform='rotate(45deg)'
            />
            <Box
              position='absolute'
              top='50%'
              left='50%'
              transform='translate(-50%, -50%)'
              fontWeight='bold'
              textShadow='0 1px 0 #fff'
            >
              {result.toFixed(2)}
            </Box>
          </Box>
        </Flex>

        <Flex position='relative' h={5}>
          <Box
            position='absolute'
            top={0}
            left={0}
            right={0}
            bottom={0}
            borderRadius='sm'
            boxShadow='0 0 0 8px token(colors.neutral.100)'
            overflow='hidden'
            pointerEvents='none'
          >
            <Box
              position='absolute'
              top={0}
              left={0}
              w='full'
              h='full'
              bg={condition === 'under' ? 'error' : 'success'}
            />
            <Box
              position='absolute'
              top={0}
              left={0}
              h='full'
              bg={condition === 'under' ? 'success' : 'error'}
              style={{
                width: `${target}%`,
              }}
            />
          </Box>

          <StyledInput
            type='range'
            min={0}
            max={100}
            value={target}
            onChange={onChange}
            disabled={isDisabled}
          />
        </Flex>
      </Box>
    </Box>
  )
})

export default DiceSlider
